import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { unwrap } from '../utils/api';

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [editingLimitId, setEditingLimitId] = useState(null);
  const [limitDraft, setLimitDraft] = useState('');

  async function load() {
    const r = await api.get('/coupons/getCoupons');
    setCoupons(unwrap(r) || []);
  }
  useEffect(() => { load(); }, []);

  async function toggleActive(c) {
    await api.post(`/coupons/updateStatus/${c._id}`, { publish: !c.publish });
    load();
  }

  // Hard delete: removes the coupon row entirely. Past orders that used
  // it keep the code stored as a plain string, so the analytics page
  // still shows that "X uses of CODE12" — we're only removing the
  // coupon definition, not the redemption history.
  async function deleteCoupon(c) {
    if (!window.confirm(`Delete coupon "${c.couponCode}" permanently? This can't be undone. (Past orders that used this coupon stay intact.)`)) return;
    await api.post(`/coupons/deleteCoupon/${c._id}`);
    load();
  }

  function startEditLimit(c) {
    setEditingLimitId(c._id);
    setLimitDraft(c.usageLimit == null ? '' : String(c.usageLimit));
  }

  async function saveLimit(c) {
    const raw = limitDraft.trim();
    const value = raw === '' ? null : Math.max(0, Number(raw));
    if (raw !== '' && Number.isNaN(value)) {
      setEditingLimitId(null);
      return;
    }
    await api.post(`/coupons/updateCoupon/${c._id}`, { usageLimit: value });
    setEditingLimitId(null);
    load();
  }

  function statusLabel(c) {
    if (!c.publish) return { text: 'Inactive', color: '#888' };
    if (typeof c.usageLimit === 'number' && c.usageLimit > 0 && (c.timesUsed || 0) >= c.usageLimit) {
      return { text: 'Exhausted', color: '#b42318' };
    }
    return { text: 'Active', color: '#0a7a32' };
  }

  return (
    <>
      <h1>Coupons</h1>
      <div className="card">
        <Link className="btn" to="/pages/createCoupon">+ New Coupon</Link>
      </div>
      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Discount</th>
              <th>Used</th>
              <th>Limit</th>
              <th>Start</th>
              <th>End</th>
              <th>Status</th>
              <th>Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {coupons.map(c => {
              const s = statusLabel(c);
              return (
                <tr key={c._id}>
                  <td><strong>{c.couponCode}</strong></td>
                  <td>{c.discountPercent}%</td>
                  <td>{c.timesUsed || 0}</td>
                  <td>
                    {editingLimitId === c._id ? (
                      <input
                        type="number"
                        min="0"
                        autoFocus
                        value={limitDraft}
                        onChange={e => setLimitDraft(e.target.value)}
                        onBlur={() => saveLimit(c)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveLimit(c);
                          if (e.key === 'Escape') setEditingLimitId(null);
                        }}
                        placeholder="∞"
                        style={{ width: 80 }}
                      />
                    ) : (
                      <span
                        onClick={() => startEditLimit(c)}
                        title="Click to edit. Leave blank for unlimited."
                        style={{ cursor: 'pointer', borderBottom: '1px dashed #aaa' }}
                      >
                        {c.usageLimit == null || c.usageLimit === 0 ? '∞' : c.usageLimit}
                      </span>
                    )}
                  </td>
                  <td>{c.startDate ? new Date(c.startDate).toLocaleDateString() : '—'}</td>
                  <td>{c.endDate ? new Date(c.endDate).toLocaleDateString() : '—'}</td>
                  <td style={{ color: s.color, fontWeight: 600 }}>{s.text}</td>
                  <td>
                    <button
                      type="button"
                      onClick={() => toggleActive(c)}
                      aria-pressed={c.publish}
                      title={c.publish ? 'Deactivate coupon' : 'Activate coupon'}
                      style={{
                        position: 'relative',
                        width: 48,
                        height: 26,
                        border: 'none',
                        borderRadius: 13,
                        background: c.publish ? '#0a7a32' : '#cbd2d9',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: 3,
                          left: c.publish ? 25 : 3,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: '#fff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                          transition: 'left 0.18s',
                        }}
                      />
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn small danger"
                      onClick={() => deleteCoupon(c)}
                      title="Permanently delete this coupon"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
